import { CSSProperties } from "react";
import dayjs from "./dayjs";

/**
 * Autocomplete dropdown styling constants
 * These constants ensure consistent padding across all autocomplete dropdown items
 */
export const AUTOCOMPLETE_ITEM_PADDING = "0.25rem 1rem";
export const AUTOCOMPLETE_GROUP_HEADER_PADDING = "0.25rem 1rem 0.25rem 0.5rem";
export const AUTOCOMPLETE_GROUP_ITEM_PADDING = "0.25rem 1rem 0.25rem 2rem";

export const AUTOCOMPLETE_ITEM_PADDING_STYLE = {
  padding: AUTOCOMPLETE_ITEM_PADDING
} as const;

export const AUTOCOMPLETE_ITEM_PADDING_STYLE_CLICKABLE = {
  cursor: "pointer",
  padding: AUTOCOMPLETE_ITEM_PADDING
} as const;

export interface UrlParser {
  extractKeyAndValue: any;
  parseUrl: any;
  getPageFromCurrentLocation : (location: string) => string
}

/**
  * PageState will hold string values not objects.
  * PageFiltersState will be the one holding dropdowns/filter objects
 */
export interface PageState {
  page: string | null;
  dateStart: dayjs.Dayjs | null;
  dateEnd: dayjs.Dayjs | null;
  publisherIds: string[] | null;
  siteIds: string[] | null;
  placementIds: string[] | null;
  trafficSourcesIncludeExclude: IncludeExclude;
  placementsIncludeExclude: IncludeExclude;
  trafficTypes: string | null;
  trafficTypesIncludeExclude: IncludeExclude;
  countries: string | null;
  countriesIncludeExclude: IncludeExclude;
  groupBy: string | null;
  productIds: string | null;
  productsIncludeExclude: IncludeExclude;
  campaignIds: string | null;
  campaignIdsIncludeExclude: IncludeExclude;
  demandSourceIds: string | null;
  demandSourceIdsIncludeExclude: IncludeExclude;
  queryFlags: QueryFlags[] | null;
  cLinks: string[] | null;
  cLinksIncludeExclude: IncludeExclude;
  viewAs: string | null;
  pivotBy: string | null;
  dateMode: DateModes;
  performanceStatsViewMode: 'trafficSource' | 'demand' | null; // For performance stats page view toggle
}
export interface UserClinks {
  clinkAssignable: boolean | null;
  allowedPlacements: [] | null;
  clinkEditScope: string[] | null;
  clinkViewAccess: Object | null;
}

export interface UserState {
  accountCreated: string | null;
  accountRefreshed: string | null;
  accountUpdated: string | null;
  actualUser: Object | null;
  clinks: UserClinks | null;
  email: string | null;
  filterFields: string[] | null;
  groupByFields: string[] | null;
  name: string | null;
  permissions: string[] | null;
  authUserPermissions: AuthUserPermissions[] | null;
  picture: string | null;
  userId: string | null;
  userIdReference: string | null;
}

export interface SortState {
  columnIdSorted: string;
  sort: number;
}
export interface ProductsAndCampaigns {
  dto: ProductsCampaignsDto | null;
}

export interface DatagridColumn {
  id: ColumnIds;
  name: string;
  align: "left" | "center" | "right" | "justify" | "char" | undefined;
  type: DataType;
  style: CSSProperties;
  visible: boolean;
  showQuickFilterMenu: boolean;
  isGroupByColumn: boolean;
  precision?: number;
  axisId?: string;
  avgDivideByNumDate?: boolean;
}

export interface PublisherSiteRecord {
  apptapPublisherId: string;
  apptapSiteId: string;
  apptapPublisherName: string;
  apptapPublisherSiteName: string;
  publisherRevShare?: number;
  clinkable?: string;
  retired: boolean;
  active: boolean;
}

export interface PublisherRecord {
  apptapPublisherId: string;
  apptapPublisherName: string;
  retired: boolean;
  publisherSites: PublisherSiteRecord[];
  publisherRevShare?: number;
}

export interface PlacementRecord {
  apptapPublisherId: string;
  apptapSiteId: string;
  publisherPlacementId: string;
  publisherPlacementName: string;
}

export interface PublisherOrSiteOption {
  record: PublisherRecord|PublisherSiteRecord;
  key: string;
  text: string;
  id : string; //This one is needed internally by the autocomplete to remove duplicates.
  disabled?: boolean;
}

export interface CLinkRecord {
  apptapPublisherId: string;
  apptapPublisherName: string;
  apptapSiteId: string;
  cLinkId: string;
  owner: string;
  productId: string;
  tags?: string[];
}

export interface CLinkOwnerOption {
  record: string | CLinkRecord;
  key: string;
  text: string;
  id : string;
}

export interface ProductRecord {
  // as of 2022-02-09 - these are what's being returned by the server
  id: string;
  name: string;

  // as of 2022-02-09 - these are what we want returned by the server - will be migrated client-side for now
  icon: string;
  productId: string;
  productName: string;
  productPlatform: string;
}

export enum IncludeExclude {
  Include="Include",
  Exclude="Exclude"
}

export enum CampaignStatus {
  Active="Active",
  Paused="Paused",
  Unknown="Unknown"
}

export interface CampaignRecord {
  productId: string
  demandSourceId: string;
  demandSourceName: string;
  demandSourcePartnerName: string;
  campaignId: string;
  campaignStatus: CampaignStatus;
  labelKey?: string;
  customOption?: boolean;
  title?: string;
}

export interface DemandSourceRecord {
  demandSourceId: string;
  demandSourceName: string;
  demandSourcePartnerName: string;
}

export interface TrafficSourceRecord {
  publisherId: string;
  siteId: string | null;
  publisherName: string;
  siteName: string | null;
}

export interface DropdownState {
  data : any | null;//This is meant to be an object.  * z+
  // reactRef : any;
  setState : any;
}

export interface DateRange {
  dateStart : dayjs.Dayjs | null;
  dateEnd : dayjs.Dayjs | null;
}

export interface ProductsDropdownState {
  data : ProductRecord[] | null;//This is meant to be an object.  * z+
  // reactRef : any;
  setState : any;
}
 
export interface IdName {
  id : string | null;
  name : string | null;
  required?: boolean;
}

export interface CategoricalCellDto {
  apptapPlacementId : string;
  publisherPlacementId : string;
  siteId : string;
  publisherId : string;
  publisherName : string;
  campaignSource : string;
  campaignId : string;
  productId : string;
  cLinkOwner: string,
  cLinkId: string,
  productsResponse: ProductRecord[];
  campaignsResponse: CampaignRecord[];
  columnId : string;
  page : string;
  trafficSourceResponseDto : TrafficSourceResponseDto;
  setTrafficSourceResponseDto : any;
  setProductsCampaignsDto : any;
}

export enum ColumnIds {
  //categorical
  hour="hour",
  date="date",
  week="week",
  month="month",
  apptapPlacementID="apptapPlacementId",
  apptapPublisherName="apptapPublisherName",
  apptapPublisherID="apptapPublisherId",
  apptapSiteID="apptapSiteId",
  publisherPlacementID="publisherPlacementId",
  trafficType="trafficType",
  country="country",
  apptapPlacementId="apptapPlacementId",
  bidStatus="bidStatus",
  cLinkId="cLinkId",
  cLinkOwner="cLinkOwner",
  cLinkTags="cLinkTags",
  apptapOfferId="apptapOfferId",
  untrackedReason="untrackedReason",
  creativeId="creativeId",
  campaignEventType="campaignEventType",
  campaignEventId="campaignEventId",
  algorithmSplit="algorithmSplit",
  designSplit="designSplit",
  tags="tags",

  campaignSource="campaignSource",
  productID="productId",
  campaignID="campaignId",

  //numerical
  pageLoads="pageLoads",
  pageViews="pageViews",
  clicksReceived="clicksReceived",
  clicksTracked="clicksTracked",
  clicksWithReportedCost="clicksWithReportedCost",
  conversionsReceived="conversionsReceived",
  conversionsCredited="conversionsCredited",
  revenueApptap="revenueApptap",
  revenuePublisher="revenuePublisher",
  productViews="productViews",
  campaignEventsReceived="campaignEventsReceived",
  lastUpdate="lastUpdate",

  //calculated
  avgCPA="avgCPA",
  eCPC="eCPC",
  trackedCreditRate="trackedCreditRate",
  publisherTrackedCreditRate="publisherTrackedCreditRate",
  apptapMargin="apptapMargin",
  reportedClickCost="reportedClickCost",
  effCostPerClick="effCostPerClick",
  apptapNetRevenue="apptapNetRevenue",
  untrackedClicksRate="untrackedClicksRate",
  otherEventsReceived="otherEventsReceived",
  returnOnAdvertisingSpend="returnOnAdvertisingSpend",
  avgColumn="avgColumn",
  otherEventsRate="otherEventsRate",
  avgPPC="avgPPC",

  //performance reports : connexity
  postConversionEvents="postConversionEvents",
  demandAccountID="demandAccountId",
  demandPlacementID="demandPlacementId",
  postConversionEventRate="postConversionEventRate",

  //demand source fields (from /2/stats/overall endpoint)
  demandSourceId="demandSourceId",
  demandSourceName="demandSourceName",

  //RTB (Real-Time Bidding) fields
  rtbRequests="rtbRequests",
  rtbFills="rtbFills",
  rtbTotalBid="rtbTotalBid",

  //performance reports: amazon
  productItemID="productItemId",
  productItemName="productItemName",

  estimatedAdvertiserSales="estimatedAdvertiserSales",
  targetAdvertiserSales="targetAdvertiserSales",
  costOfSale="costOfSale",
  targetCostOfSale="targetCostOfSale",

  //auxiliar column used for pivot
  fields="fields",
}

export enum PageName {
  home="home",
  overall="overall",
  products="products",
  performanceStats="performanceStats",
  performanceReports="performanceReports",
  trackingLinksLookup="trackingLinksLookup",
  lookup="lookup",
  liveLogs="liveLogs",
  inventory="inventory",
  authUser="authUser"
}

export interface ResetPageDto {
  trafficSourcesRef: any;
  countriesRef: any;
  trafficTypesRef: any;
  groupByRef: any;
  placementsRef: any;
  showMonetizableRef: any;
  showWithRevenueRef: any;
  showWithConversionsRef: any;
  productsRef: any;
  campaignsRef: any;
  demandSourcesRef: any;
  cLinksRef: any;
  queryFlagsRef: any;
}


export enum ErrorType{
  exception = 1,
  warning = 2,
  info = 3
}

export interface ErrorData {
  msg : string ;
  type : ErrorType;
}
 
export enum ResponseStatus{
  OK = 1,
  ERROR = 2,
  LOADING = 3
}

export interface GenericResponseDto {
  status : ResponseStatus; 
  error : ErrorData | null;
}

export interface TrafficSourceResponseDto extends GenericResponseDto {
  publishersArray : PublisherOrSiteOption[];
}

export interface DemandSourcesResponseDto extends GenericResponseDto {
  demandSourcesResponse : DemandSourceRecord[];
}

export interface ProductsCampaignsDto extends GenericResponseDto {
  productsResponse : ProductRecord[];
  campaignsResponse : CampaignRecord[];
}

export interface PlacementResponseDto extends GenericResponseDto {
  placementsResponse : PlacementRecord[];
}

export interface CLinkOwnerResponseDto extends GenericResponseDto {
  cLinksOwnerResponse : CLinkOwnerOption[];
}

export interface UserListResponseDto extends GenericResponseDto {
  userListResponse : UserState[];
}

export interface SearchResponseDto extends GenericResponseDto {
  data : any[],
  query : any,
  metadata : any
}

export interface TimezoneInfo {
  utcOffsetMinutes: string;
}
export interface ReachExtensionInfo {
  effectivePartnerPlacement: string;
}
export interface HttpHeaderInfo {
  geoIPCountry: string;
  userIP: string;
}
export interface AppImpressionInfo {
  appID: string;
}
export interface Click {
  timestamp: string;
  clientTimezoneInfo?: TimezoneInfo;
  globalUserId: string;
  clickId: string;
  publisherId: string;
  siteId: string;
  reachExtensionInfo: ReachExtensionInfo;
  trackingPartner: string;
  httpHeaderInfo: HttpHeaderInfo;
  appImpressionInfo: AppImpressionInfo;
  campaignId: string;
  campaignLinkID: string;
}

export interface Conversion extends Click {
  timestampReceived: string;
  clickTime: string;
  partnerEventID: string;
  costPerInstall: number;
  partnerPayoutForInstall: number;
  
}
export interface LiveLogsData {
  clicks: Click[];
  conversions: Conversion[];
}
export interface LiveLogsResponseDto extends GenericResponseDto {
  data: LiveLogsData
}

/**
 * {
 *  "status": "PAUSED",
		"state": "PAUSED",
		"health": "HEALTHY",
		"isQCBroken": false,
		"supplySource": "linkconnector",
		"appID": "tile.buymbs",
		"campaignID": "APT2407398",
		"plutoCampaignID": 2407398,
		"plutoSystem": "prod",
		"manualCampaign": true,
		"dispCampaignID": "APT2407398",
		"platforms": "android",
		"countries": "US",
		"tracker": "linkconnector",
		"categories": "shopping",
		"payout": "0.1",
		"payoutAction": "CPC",
		"knownEvents": [
			{
				"grossCPA": 0.1,
				"eventDesc": "click",
				"eventSourceType": "FEED_WELL_DEFINED",
				"eventType": "CLICK",
				"eventTypeCode": "CPC",
				"isDefault": true,
				"maxPayable": 1,
				"dailyCap": 10000
			}
		],
		"appFoundInCatalog": true,
		"appTapCampaignID": "KtpOS4P4p_JsuxUeFvAYtA",
		"incent": false,
		"enddate": "n/a",
		"startdate": "2022-02-09T00:00:00Z",
		"buyLink": "https://api.apptap.com//link/buy/android/tile.buymbs?pubID=che7ratrap3u_atap&siteID=campaign_lookup&campaignID=KtpOS4P4p_JsuxUeFvAYtA&imprID=APPTAPADMIN",
		"osVersion": "1+",
		"limit": "None",
		"deviceIDRequired": false,
		"storeLink": "https://buymbs.com/",
		"placementsAllowed": [
			"***"
		],
		"placementsBlocked": [],
		"newSince": "",
		"dailyInstallCap": 0,
		"sourceAppIDRequired": false,
		"sourceAppNameRequired": false,
		"campaignMatch": true,
		"optimizations": {
			"publishers": [
				{
					"publisherID": "*** (all publishers)",
					"allows": [
						{
							"publisherID": "*** (all publishers)",
							"siteID": null,
							"placementID": null,
							"whitelisted": true,
							"blocked": false
						}
					],
					"blocks": []
				}
			],
			"hasWhitelist": true
		}
 * }
 */
export interface CampaignKnownEvent {
  grossCPA?: number;
  eventDesc?: string;
  eventType?: string;
  eventTypeCode?: string;
  isDefault?: boolean;
  maxPayable?: number;
  dailyCap?: number;
}

export interface CampaignsResponseDtoCampaign {
  campaignID: string;
  supplySource: string;
  supplySourceName: string;
  added?: boolean;
  countries: string;
  platforms: string;
  osVersion: string;
  dailyInstallCap?: number;
  incent: boolean;
  payout: number;
  status: string;
  limit: "None"|number;
  enddate: string;
  deviceIDRequired: boolean;
  knownEvents?: CampaignKnownEvent[];
}
export interface CampaignsResponseDtoUnconfiguredCampaign {
  campaign_id: string;
  source: string;
  source_name: string;
  added?: boolean;
  incent: boolean;
}

export interface CampaignsResponseDto extends GenericResponseDto {
  campaigns: CampaignsResponseDtoCampaign[],
  unconfiguredCampaigns: CampaignsResponseDtoUnconfiguredCampaign[],
  clinks: ClinkFromServer[],
}

export interface Clink {
  clinkId: string;
  status: ClinkStatus;
  pubId: string;
  siteId: string;
  appId: string;
  platforms: string,
  android: boolean;
  iphone: boolean;
  ipad: boolean;
  p2p: number;
  minPayout: number;
  countries: CountryOption[];
  dailyCap:  number;
  creator: string;
  campaigns: CampaignsResponseDtoUnconfiguredCampaign[];
  created: dayjs.Dayjs;
  lastUpdate: dayjs.Dayjs;
  incent: boolean;
  tags: string[];
  ignorePlatforms: boolean;
  owner: string;
  previewLink: string;
  allowCounterBidOverride: string;
  allowCounterBid: string;
  useRevShareP2p: boolean;
}

/**
 * Health status for cLinks (campaign links)
 * - Green: Fully operational, all campaigns active
 * - Yellow: Warning state, one or more campaigns inactive
 * - Red: Critical, cannot serve traffic
 * - None: Status unknown or unavailable
 */
export type ClinkHealth = 'Green' | 'Yellow' | 'Red' | 'None';

/**
 * Status for cLinks (campaign links)
 * Can be uppercase (backend) or lowercase (frontend)
 */
export type ClinkStatus = 'ACTIVE' | 'PAUSED' | 'ENDED' | 'active' | 'paused' | 'ended';

export interface ClinkFromServer {
  app_id: string;
  clink_id: string;
  configs?: ClinkConfig[];
  created: string;
  creator: string;
  health: ClinkHealth;
  incent: boolean;
  last_update: string;
  owner: string;
  previewLink: string;
  pub_id: string;
  pub_name: string;
  publisher_rev_share?: number;
  site_id: string;
  stats: ClinkStats;
  clinkMatch?: boolean;
  campaignIds?: string[];
}
export interface ClinkConfig {
  allow_counter_bid_override: boolean;
  campaigns: CampaignsResponseDtoUnconfiguredCampaign[];
  countries: string;
  daily_cap: number;
  ignorePlatforms: boolean;
  min_payout: number;
  p2p: number;
  platforms: string;
  status: ClinkStatus;
  ts: string;
  updated_by: string;
  user_rev_share_p2p: string;
}

export interface ClinkStats {
  clicks: number;
  dayBuys: number;
  dayClicksCost: number;
  dayClicksWithCost: number;
  dayInstalls: number;
  dayPublisherPayout: number;
  dayRTBFillRate: number;
  dayRTBFills: number;
  dayRTBRequests: number;
  dayRevenue: number;
  dayTracked: number;
  dayUntracked: number;
  installs: number;
  recentRTBFillRate: number;
  recentRTBFills: number;
  recentRTBRequests: number;
  todayBuys: number;
  todayClicksCost: number;
  todayClicksWithCost: number;
  todayInstalls: number;
  todayPublisherPayout: number;
  todayRTBFillRate: number;
  todayRTBFills: number;
  todayRTBRequests: number;
  todayRevenue: number;
  todayTracked: number;
  todayUntracked: number;
  type: string;
  weekBuys: number;
  weekClicksCost: number;
  weekClicksWithCost: number;
  weekInstalls: number;
  weekPublisherPayout: number;
  weekRTBFillRate: number;
  weekRTBFills: number;
  weekRTBRequests: number;
  weekRevenue: number;
  weekTracked: number;
  weekUntracked: number;
}

export interface ClinkResponseDto extends GenericResponseDto {
  clink: Clink
}

export interface ClinkNotesResponseDto extends GenericResponseDto {
  clinkNotes: ClinkNote[]
}

export interface InventoryItem {
  demandSourceId: string;
  countryCode: string;
  domain: string;
  trackingUrl: string,
  eepc: number,
  valid: boolean
}
export interface ExtendedInventoryItem extends InventoryItem {
  demandSourceName: string;
}
export interface InventoryResponseDto extends GenericResponseDto {
  inventory: InventoryItem[];
  count?: number;
}

/**
 * Basic app information for UI components
 */
export interface App {
  appID: string;
  icon?: string;
  title: string;
}

/**
 * Extended app data from Jupiter API /admin/campaigns/query endpoint
 * Includes campaigns, clinks, and statistics
 */
export interface AppExtended extends App {
  category: string | null;
  maturity: string;
  storeLink: string;
  bannerLink: string | null;
  tagLine: string | null;
  newSince: string;
  creatives: number;
  countries: string;
  payoutMin: string;
  payoutMax: string;
  campaigns: CampaignsResponseDtoCampaign[];
  clinks: ClinkFromServer[];
  stats?: any;
  counts?: {
    appCampaigns: number;
    appMatchedCampaigns: number;
    appClinks: number;
    appMatchedClinks: number;
  };
}

export interface Validation {
  [key: string]: boolean;
}

export interface User {
  loginEmail: string;
  name: string|null;
  allowedPlacements: string[];
  picture: string;
}

export enum UserPermissions {
  adminSuperUser = "admin:super-user",
  apptapUsersList = "apptap:users:list",
  apptapDataCampaigns= "apptap:data:campaigns",
  apptapDashboardsViewAsAnyUser="apptap:dashboards:view_as_any_user",
  appptapDataPerformance="apptap:data:performance:demand",
  appptapDataFullAccess="apptap:data:full-access",
  appptapDataCLinks="apptap:data:clinks",
  appptapDataCLinksOwners="apptap:data:clinks:owners",
  appptapDataCLinksTags="apptap:data:clinks:tags",
  appptapDataCLinksNotes="apptap:data:clinks:notes",
  appptapDataRtb="apptap:data:rtb",
  appptapDataGrossRevenue="apptap:data:gross-revenue",
  appptapDashboardsAffiliateDashboard="apptap:dashboards:affiliate_dashboard",
  appptapDashboardsCampaignLinks="apptap:dashboards:campaign_links",
  appptapDashboardsCampaignLookup="apptap:dashboards:campaign_lookup"
}

export enum AuthUserPermissions {
  userView = "user-view",
  userAdd = "user-add",
  userEdit = "user-edit",
  userDelete = "user-delete"
}

export interface PivotGroupByOption {
  id: string;
  name: string;
  dateStart: dayjs.Dayjs | null;
  dateEnd: dayjs.Dayjs | null;
  dateMode: DateModes;
}

export type FixedDateRange = [dayjs.Dayjs, dayjs.Dayjs];

export interface FixedDateRanges {
  today: FixedDateRange,
  dateStart:dayjs.Dayjs,
  dateEnd: dayjs.Dayjs,
  yesterday: FixedDateRange,
  last7Days: FixedDateRange,
  last10Days: FixedDateRange,
  last14Days: FixedDateRange,
  last30Days: FixedDateRange,
  thisMonth: FixedDateRange,
  lastMonth: FixedDateRange,

  thisWeek: FixedDateRange,
  lastWeek: FixedDateRange,
  thisMonthWeeks: FixedDateRange,
  lastMonthWeeks: FixedDateRange,
  last12Weeks: FixedDateRange,

  thisYear: FixedDateRange,
  lastYear: FixedDateRange,
  last6Months: FixedDateRange,
  last12Months: FixedDateRange,

  month1: dayjs.Dayjs,
  month2: dayjs.Dayjs,
  year1: dayjs.Dayjs,
  year2: dayjs.Dayjs,

  minDate: dayjs.Dayjs,
  maxDate: dayjs.Dayjs,
}

export interface DateRangePickerState extends FixedDateRanges {
  previousDateStart: dayjs.Dayjs,
  previousDateEnd: dayjs.Dayjs,
  isFirstClickInDateRange: boolean,
  hover: dayjs.Dayjs,
  dateMode: DateModes,
}


export interface Message {
  message: string,
  type: MessageType
}

export interface ClinkNote {
  uuid: string;
  message: string;
  last_update: dayjs.Dayjs;
  created_by: string;
}

export interface CountryOption {
  id: string,
  name: string
}

export interface CampaignStatsOption {
  id: string,
  name: string,
  category: 'state' | 'region'
}

export interface CampaignHealthOption {
  id: string,
  name: string,
  category: 'qc' | 'stats'
}

export interface ClinksStatusOption {
  id: string,
  name: string,
  category: 'app' | 'campaign'
}

export interface ActivityStatusOption {
  id: string,
  name: string,
  category: 'clicks' | 'conversions' | 'cap'
}

export interface QueryString {
  dateStart: string;
  dateEnd: string;
  groupBy?: string;
  placementsInclude?: string;
  placementsExclude?: string;
  trafficTypesInclude?: string;
  trafficTypesExclude?: string;
  countriesInclude?: string;
  countriesExclude?: string;
  queryFlags?: string;
  clinksInclude?: string;
  productsInclude?: string;
  productsExclude?: string;
  campaignsInclude?: string;
  campaignsExclude?: string;
  campaignIdsInclude?: string;
  campaignIdsExclude?: string;
}

export enum PivotMode {
  NO_PIVOT = 1,
  PIVOT_GROUP_BY = 2,
  PIVOT_ALL_FIELDS = 3
}

export enum PivotIds {
  off="off",
  current="current",
  last7days="last7days",
  last14days="last14days",
  last12weeks="last12weeks",
  last90days="last90days",
  last12months="last12months",
  last2years="last2years"
}

export enum QueryFlags {
  CampaignsOnlyMonetizeable = "CampaignsOnlyMonetizeable",
  ResultRowsWithRevenue = "ResultRowsWithRevenue",
  ResultRowsWithConversions = "ResultRowsWithConversions"
}
export const QueryFlagLabels: { [key in QueryFlags]: string } = {
  [QueryFlags.CampaignsOnlyMonetizeable]: "Only Monetizeable Campaigns",
  [QueryFlags.ResultRowsWithRevenue]: "Only Results with Revenue",
  [QueryFlags.ResultRowsWithConversions]: "Only Results with Conversions",
};
export const QueryFlagTooltips: { [key in QueryFlags]: string } = {
  [QueryFlags.CampaignsOnlyMonetizeable]: "Limits search to campaigns which were monetizeable (excludes organic campaigns). Monetizeable includes both tracked and untracked clicks, where a campaign or cLink was available.",
  [QueryFlags.ResultRowsWithRevenue]: "Only returns result rows where there is at least one conversion.",
  [QueryFlags.ResultRowsWithConversions]: "Only returns result rows where there is a non-zero revenue amount.",
};

export const TrafficTypeMap: {[key: string]: string} = {
  "all-mobile": "mobile-android,mobile-ios,mobile-other",
  "mobile-android": "mobile-android",
  "mobile-ios": "mobile-ios",
  "mobile-other": "mobile-other",
  "all-desktop": "desktop-other,desktop-NoUserAgent",
  "desktop-other": "desktop-other",
  "desktop-NoUserAgent": "desktop-NoUserAgent"
};

export enum MessageType {
  DANGER = "danger",
  WARNING = "warning",
  INFO = "info"
}

export enum DataType {
  STRING = "string",
  NUMBER = "number",
  MONEY = "money",
  PERCENTAGE = "percentage",
  DATE = "date",
  WEEK = "week",
  MONTH = "month",
  TAG = "tag"
}

export enum PayoutMode {
  FIXED = "fixed",
  REV_SHARE = "revShare",
  NO_PAYOUT = "noPyout"
}

declare global {
  interface Window {
    pubID: any,
    siteID: any,
    permissions: any,
    current_user: any,
    apptapAdmin: any
  }
}

export interface UserInfo {
  permissions: UserPermissions[],
  authUserPermissions: AuthUserPermissions[],
  filterFields: string[],
  groupByFields: string[],
  picture: string,
  email: string
}

export interface MenuItem {
  link?: string;
  id?: string;
  page?: string;
  disabled?: boolean;
  admin?: boolean;
}

export interface CampaignSummary {
  numberOfCampaigns: number,
  totalCap: number,
  countries: string[],
  platforms: string[],
  osVersion: string,
  incent: boolean,
  minPayout: number,
  maxPayout: number,
  payoutRange: string
}
export type DateModes = "days" | "weeks" | "months";

export interface AuthUser {
    timezoneId: number;
    id: number;
    name: string;
    email: string;
    auth0: string;
    emailVerified: boolean;
    active: number;
    roles: UserRoleAssignment[];
    permissions: UserPermissionAssignment[];
}

export interface UserRoleAssignment {
    roleId: number;
    roleName: string;
    roleSlug: string;
    contextId: number | null;
    contextName: string | null;
    contextIdKey: string | null;
}

export interface UserPermissionAssignment {
    permissionId: number;
    permissionName: string;
    permissionSlug: string;
    contextId: number | null;
    contextName: string | null;
    contextIdKey: string | null;
}

export interface Role {
    id: number;
    name: string;
    slug: string;
    contextTypeId: number | null;
    contextTypeName: string | null;
    permissions: Permission[];
}

export interface Permission {
    id: number;
    name: string;
    slug: string;
    contextTypeId: number | null;
    contextTypeName: string | null;
    description: string | null;
}

export interface SecurityContext {
    id: number;
    name: string;
    slug: string;
}

export interface ContextResource {
    id: number;
    idKey: string;
    name: string;
    contextType: string;
}

export interface AuthUserListResponse {
    users: AuthUser[];
    total: number;
}

export interface TimezoneResponse {
    id: number;
    code: string;
    zone: string;
}
